import { type ReactNode, useMemo, useState } from "react";
import { v4 } from "uuid";
import { Loader2, Plus } from "lucide-react";
import t from "@/lang/lang";
import { LocationType } from "@/services/backend/buildings/types/location-type";
import { levelsOfType } from "@/services/backend/buildings/lib/building-areas";
import {
  useBuildingsAreaCreateMutation,
  useBuildingsAreaUpdateMutation,
} from "@/services/backend/buildings/api/building-api";
import { type Area } from "@/services/backend/buildings/types/area";
import { type Building } from "@/services/backend/buildings/types/building";
import { parseRTKQueryErrorToSonner } from "@/shared/components/errors/parse-r-t-k-query-error";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { usePersistenceContext } from "@/shared/lib/persistence-state/context";
import { PersistenceState } from "@/shared/lib/persistence-state/persistence-state";
import { toast } from "sonner";
import { TextInput } from "@/shared/components/form/text-input";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { NullUUID } from "@bitory-gmbh/nidavellir";

/**
 * Creates or edits an area. Levels are picked from the ones the building already
 * has; a new level is created through the level manager, not from here.
 */
export function AreaDialogButton({
  building,
  defaultType,
  triggerLabel,
  triggerIcon,
  triggerVariant = "default",
  area,
  iconOnly = false,
}: {
  building: Building;
  defaultType: LocationType;
  triggerLabel: string;
  triggerIcon?: ReactNode;
  triggerVariant?: "default" | "outline";
  area?: Area;
  iconOnly?: boolean;
}) {
  const { setPersistenceState } = usePersistenceContext();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(area?.name ?? "");
  const [type, setType] = useState<LocationType>(area?.type ?? defaultType);
  const [levelId, setLevelId] = useState(area?.levelId ?? "");
  const [createArea, createState] = useBuildingsAreaCreateMutation();
  const [updateArea, updateState] = useBuildingsAreaUpdateMutation();
  const saveState = area ? updateState : createState;

  const availableLevels = useMemo(
    () => levelsOfType(building, type),
    [building, type],
  );

  const handleSave = () => {
    const put = area ? updateArea : createArea;
    const request = {
      id: building.id,
      areaId: area ? area.id : v4(),
      name,
      type: area ? area.type : type,
      levelId: levelId || undefined,
    };
    put(request)
      .unwrap()
      .then(() => {
        setOpen(false);
        saveState.reset();
        setPersistenceState(PersistenceState.SAVED);
      })
      .catch((err) => {
        setPersistenceState(PersistenceState.ERROR);
        toast(...parseRTKQueryErrorToSonner(err));
      });
    setPersistenceState(PersistenceState.SAVING);
  };

  const handleOpenChange = (value: boolean) => {
    if (!value) {
      setName(area?.name ?? "");
      setType(area?.type ?? defaultType);
      setLevelId(area?.levelId ?? "");
    }
    setOpen(value);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant={triggerVariant} size={iconOnly ? "sm" : "default"}>
          {triggerIcon ?? <Plus className="h-4 w-4" />}
          {!iconOnly ? <span>{triggerLabel}</span> : null}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {area ? t("Bereich bearbeiten") : t("Bereich anlegen")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "Ein Bereich kann einer vorhandenen Ebene zugeordnet werden oder ohne Ebene bleiben.",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <TextInput
            label={"Bereichsname"}
            field="name"
            text={name}
            onTextChange={setName}
            errors={inputValidationErrorMessages(saveState.error, "name")}
          />
          <div className="grid gap-2">
            <Label>{t("Bereichstyp")}</Label>
            <Select
              value={type}
              onValueChange={(value) => {
                setType(value as LocationType);
                setLevelId("");
              }}
              disabled={!!area}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("Bereichstyp auswählen")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={LocationType.LocationTypeInside}>
                  {t("Innenbereich")}
                </SelectItem>
                <SelectItem value={LocationType.LocationTypeOutside}>
                  {t("Außenbereich")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>{t("Ebene")}</Label>
            <Select
              value={levelId || NullUUID}
              onValueChange={(value) =>
                setLevelId(value === NullUUID ? "" : value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={t("Ohne Ebene")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NullUUID}>{t("Ohne Ebene")}</SelectItem>
                {availableLevels.map((level) => (
                  <SelectItem key={level.id} value={level.id}>
                    {level.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-muted-foreground text-sm">
              {availableLevels.length === 0
                ? t(
                    "Für diesen Bereichstyp ist noch keine Ebene vorhanden. Ebenen werden über „Ebenen“ angelegt.",
                  )
                : t("Die Ebene ist optional.")}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t("Abbrechen")}
          </Button>
          <Button onClick={handleSave} disabled={saveState.isLoading}>
            {saveState.isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : area ? (
              t("Speichern")
            ) : (
              t("Bereich anlegen")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
